/* eslint-disable no-useless-escape */
import { CustomField } from "../../api/CustomFields/CustomFields";
import * as yup from "yup";
import { AnySchema } from "yup";
import { CustomFieldForm } from "./CustomFieldsForm";
import { convertPathValueToDate } from "../../common/formfields/DatePickerFormField/SingleDatePickerFormField";
import { isISODate, isTime, isValidDate } from "../../common/datetime/DateTimeFormatter";
import { RequireKeys } from "../../api/GenericTypes";
import { CustomFieldDataType, CustomFieldDataTypeUtils } from "../../api/CustomFields/CustomFieldsBase";

export const AbstractedCustomFieldDataTypesConsts = [
  "Text",
  "Number",
  "Toggle",
  "Date",
  "Time",
  "Entity Reference",
  "Barcode",
  "Attachment",
  "URL",
] as const;

export type AbstractedCustomFieldDataTypes = (typeof AbstractedCustomFieldDataTypesConsts)[number];

export const EntitySelectOptionsConsts = [
  "Dataset",
  "Sample",
  "Project",
  "Person",
  "Method",
  "SharedContent",
  "LabNotebook",
  "LabNotebookExperiment",
  "LabNotebookEntry",
  "InventoryItem",
] as const;

export type EntitySelectOptions = (typeof EntitySelectOptionsConsts)[number];

export interface GenerateAbstractedFromDataTypeReturn {
  abstractedDataType?: AbstractedCustomFieldDataTypes;
  isMulti?: boolean;
  isFloat?: boolean;
  isDateTime?: boolean;
  isDateTimeRange?: boolean;
  isTimeRange?: boolean;
  entitySelectOption?: EntitySelectOptions | undefined;
}

export const generateAbstractedFromDataType = (
  dataType?: CustomField["dataType"]
): GenerateAbstractedFromDataTypeReturn => {
  switch (dataType) {
    case "String":
      return {
        abstractedDataType: "Text",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "StringArray":
      return {
        abstractedDataType: "Text",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "Integer":
      return {
        abstractedDataType: "Number",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "IntegerArray":
      return {
        abstractedDataType: "Number",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "Float":
      return {
        abstractedDataType: "Number",
        isMulti: false,
        isFloat: true,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "FloatArray":
      return {
        abstractedDataType: "Number",
        isMulti: true,
        isFloat: true,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "Boolean":
      return {
        abstractedDataType: "Toggle",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "Date":
      return {
        abstractedDataType: "Date",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "DateArray":
      return {
        abstractedDataType: "Date",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "DateTime":
      return {
        abstractedDataType: "Date",
        isMulti: false,
        isFloat: false,
        isDateTime: true,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "DateTimeArray":
      return {
        abstractedDataType: "Date",
        isMulti: true,
        isFloat: false,
        isDateTime: true,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "Time":
      return {
        abstractedDataType: "Time",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "TimeArray":
      return {
        abstractedDataType: "Time",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "TimeRange":
      return {
        abstractedDataType: "Time",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        isTimeRange: true,
        entitySelectOption: undefined,
      };
    case "DateTimeRange":
      return {
        abstractedDataType: "Date",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: true,
        entitySelectOption: undefined,
      };
    case "Url":
      return {
        abstractedDataType: "URL",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "UrlArray":
      return {
        abstractedDataType: "URL",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "Dataset":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Dataset",
      };
    case "Sample":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Sample",
      };
    case "Project":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Project",
      };
    case "Person":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Person",
      };
    case "Method":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Method",
      };
    case "SharedContent":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "SharedContent",
      };
    case "LabNotebook":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "LabNotebook",
      };
    case "LabNotebookExperiment":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "LabNotebookExperiment",
      };
    case "LabNotebookEntry":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "LabNotebookEntry",
      };
    case "InventoryItem":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "InventoryItem",
      };
    case "DatasetArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Dataset",
      };
    case "SampleArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Sample",
      };
    case "ProjectArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Project",
      };
    case "PersonArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Person",
      };
    case "MethodArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "Method",
      };
    case "SharedContentArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "SharedContent",
      };
    case "LabNotebookArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "LabNotebook",
      };
    case "LabNotebookExperimentArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "LabNotebookExperiment",
      };
    case "LabNotebookEntryArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "LabNotebookEntry",
      };
    case "InventoryItemArray":
      return {
        abstractedDataType: "Entity Reference",
        isMulti: true,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: "InventoryItem",
      };
    case "Barcode":
      return {
        abstractedDataType: "Barcode",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    case "Attachment":
      return {
        abstractedDataType: "Attachment",
        isMulti: false,
        isFloat: false,
        isDateTime: false,
        isDateTimeRange: false,
        entitySelectOption: undefined,
      };
    default:
      return {};
  }
};

export const generateDataTypeFromAbstracted = ({
  abstractedDataType,
  isMulti,
  isFloat,
  isDateTime,
  isDateTimeRange,
  isTimeRange,
  entitySelectOption,
}: RequireKeys<GenerateAbstractedFromDataTypeReturn, "isMulti">): CustomField["dataType"] | undefined => {
  switch (abstractedDataType) {
    case "Text":
      return GenerateDataType("String", isMulti);
    case "Number":
      if (isFloat === undefined) return undefined;
      return isFloat ? GenerateDataType("Float", isMulti) : GenerateDataType("Integer", isMulti);
    case "Toggle":
      return "Boolean";
    case "Date":
      if (isDateTimeRange) return "DateTimeRange";
      if (isDateTime === undefined) return undefined;
      return isDateTime ? GenerateDataType("DateTime", isMulti) : GenerateDataType("Date", isMulti);
    case "Time":
      if (isTimeRange) return "TimeRange";
      return GenerateDataType("Time", isMulti);
    case "Entity Reference":
      if (entitySelectOption === undefined) return undefined;
      return GenerateDataType(entitySelectOption, isMulti);
    case "Barcode":
      return "Barcode";
    case "Attachment":
      return GenerateDataType("Attachment", isMulti);
    case "URL":
      return GenerateDataType("Url", isMulti);
  }
};

export const GenerateDataType = (datatype: CustomField["dataType"], isMulti: boolean): CustomField["dataType"] => {
  if (!CustomFieldDataTypeUtils.HasMultiSelect(datatype)) return datatype;
  if (!isMulti) return datatype;
  if (isMulti) {
    if (datatype === "String") return "StringArray";
    if (datatype === "Integer") return "IntegerArray";
    if (datatype === "Float") return "FloatArray";
    if (datatype === "Date") return "DateArray";
    if (datatype === "DateTime") return "DateTimeArray";
    if (datatype === "Time") return "TimeArray";
    if (datatype === "Dataset") return "DatasetArray";
    if (datatype === "Sample") return "SampleArray";
    if (datatype === "Project") return "ProjectArray";
    if (datatype === "Person") return "PersonArray";
    if (datatype === "Method") return "MethodArray";
    if (datatype === "SharedContent") return "SharedContentArray";
    if (datatype === "LabNotebook") return "LabNotebookArray";
    if (datatype === "LabNotebookExperiment") return "LabNotebookExperimentArray";
    if (datatype === "LabNotebookEntry") return "LabNotebookEntryArray";
    if (datatype === "InventoryItem") return "InventoryItemArray";
    if (datatype === "Url") return "UrlArray";
  }
  return datatype;
};

export const convertPredefinedOptionsForm2Entity = (
  values?: CustomFieldForm["predefinedOptions"],
  dataType?: CustomField["dataType"]
) => {
  if (!values) return undefined;
  if (!dataType) throw new Error("Undefined data type");

  switch (dataType) {
    case "String":
    case "StringArray":
      return convertFormValueToNativeCustomFieldDataType(values, "StringArray");
    case "Integer":
    case "IntegerArray":
      return convertFormValueToNativeCustomFieldDataType(values, "IntegerArray");
    case "Float":
    case "FloatArray":
      return convertFormValueToNativeCustomFieldDataType(values, "FloatArray");
    case "Date":
    case "DateArray":
      return convertFormValueToNativeCustomFieldDataType(values, "DateArray");
    case "DateTime":
    case "DateTimeArray":
      return convertFormValueToNativeCustomFieldDataType(values, "DateTimeArray");
    case "Time":
    case "TimeArray":
      return convertFormValueToNativeCustomFieldDataType(values, "TimeArray");
    case "Url":
    case "UrlArray":
      return convertFormValueToNativeCustomFieldDataType(values, "StringArray");
    default:
      return values;
  }
};

export const convertPredefinedOptionsEntity2Form = (
  values?: CustomFieldForm["predefinedOptions"],
  dataType?: CustomField["dataType"]
) => {
  if (!values) return undefined;
  if (!dataType) throw new Error("Undefined data type");
  let _values = values as any[];
  if (Array.isArray(_values)) return _values.map((value) => convertPredefinedOption(value, dataType));
};

const convertPredefinedOption = (value?: CustomFieldForm["predefinedOptions"], dataType?: CustomField["dataType"]) => {
  switch (dataType) {
    case "String":
    case "StringArray":
      return convertNativeCustomFieldDataTypeToFormValue(value, "String");
    case "Integer":
    case "IntegerArray":
      return convertNativeCustomFieldDataTypeToFormValue(value, "Integer");
    case "Float":
    case "FloatArray":
      return convertNativeCustomFieldDataTypeToFormValue(value, "Float");
    case "Date":
    case "DateArray":
      return convertNativeCustomFieldDataTypeToFormValue(value, "Date");
    case "DateTime":
    case "DateTimeArray":
      return convertNativeCustomFieldDataTypeToFormValue(value, "DateTime");
    case "Time":
    case "TimeArray":
      return convertNativeCustomFieldDataTypeToFormValue(value, "Time");
    case "Url":
    case "UrlArray":
      return convertNativeCustomFieldDataTypeToFormValue(value, "String");
    default:
      return value;
  }
};

export const unifyPredefinedOptions = (values?: any[] | null | undefined) => {
  if (!values) return undefined;
  return values.map((value) => {
    if (typeof value === "object" && Object.hasOwn(value, "value")) {
      return value.value;
    }
    return value;
  });
};

export const convertFormValueToNativeCustomFieldDataType = (values?: any, dataType?: CustomField["dataType"]) => {
  let _value: typeof values;
  if (values === undefined || values === null) return null;
  if (typeof values === "object" && Object.hasOwn(values, "value")) {
    _value = values.value;
  } else {
    _value = values;
  }

  if (!dataType) throw new Error("Unsupported data type");
  switch (dataType) {
    case "String":
      return _value.toString();
    case "StringArray":
      return _value?.map((value: any) => convertFormValueToNativeCustomFieldDataType(value, "String"));
    case "Integer":
      return parseInt(_value);
    case "IntegerArray":
      return _value?.map((value: any) => convertFormValueToNativeCustomFieldDataType(value, "Integer"));
    case "Float":
      return parseFloat(_value);
    case "FloatArray":
      return _value?.map((value: any) => convertFormValueToNativeCustomFieldDataType(value, "Float"));
    case "Date":
      const date = convertPathValueToDate(_value);
      if (date)
        return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(
          2,
          "0"
        )}`;
      return null;
    // return convertPathValueToDate(_value)?.toISOString().split("T").slice(0, 1)?.[0];
    case "DateArray":
      return _value
        ?.filter((v: any) => !!v)
        .map((value: any) => convertFormValueToNativeCustomFieldDataType(value, "Date"));
    case "DateTime":
      return convertPathValueToDate(_value)?.toISOString();
    case "DateTimeArray":
      return _value
        ?.filter((v: any) => !!v)
        .map((value: any) => convertFormValueToNativeCustomFieldDataType(value, "DateTime"));
    case "Time":
      return convertPathValueToDate(_value)?.toISOString().split("T").slice(-1)?.[0].replace("Z", "");
    case "TimeArray":
      return _value
        ?.filter((v: any) => !!v)
        .map((value: any) => convertFormValueToNativeCustomFieldDataType(value, "Time"));
    case "TimeRange":
      return _value;
    case "Url":
      return _value.toString();
    case "UrlArray":
      return _value?.map((value: any) => convertFormValueToNativeCustomFieldDataType(value, "Url"));
    default:
      return _value;
  }
};

export const convertNativeCustomFieldDataTypeToFormValue = (
  values?: any,
  dataType?: CustomField["dataType"]
): any | any[] | null => {
  if (!values) return null;
  if (!dataType) throw new Error("Unsupported data type");
  switch (dataType) {
    case "String":
    case "StringArray":
    case "Integer":
    case "Float":
    case "TimeRange":
    case "Url":
    case "UrlArray":
    case "Barcode":
      return values;
    case "IntegerArray":
      return Array.isArray(values) ? values.map((value: any) => +value) : null;
    case "FloatArray":
      return Array.isArray(values) ? values.map((value: any) => +value) : null;
    case "Date":
      return isISODate(values)
        ? new Date(`${values.split("T")[0]}T00:00:00.000Z`)
        : typeof values === "string"
          ? new Date(`${values}T00:00:00.000Z`)
          : values;
    case "DateArray":
      return Array.isArray(values)
        ? values.map((value: any) => convertNativeCustomFieldDataTypeToFormValue(value, "Date"))
        : null;
    case "DateTime":
      return typeof values === "string" ? new Date(values) : values;
    case "DateTimeArray":
      return Array.isArray(values)
        ? values.map((value: any) => convertNativeCustomFieldDataTypeToFormValue(value, "DateTime"))
        : null;
    case "Time":
      return typeof values === "string" ? new Date(`1970-01-01T${values}Z`) : values;
    case "TimeArray":
      return Array.isArray(values)
        ? values.map((value: any) => convertNativeCustomFieldDataTypeToFormValue(value, "Time"))
        : null;
    default:
      return values;
  }
};

const maxCustomFieldArray = 1000;
const maxCustomFieldStringChars = 10000;
export const maxCustomFieldTextAreaChars = 30000;

export const convertCustomFieldDataTypeToYupShape = (dataType: CustomField["dataType"], label?: string): AnySchema => {
  switch (dataType) {
    case "String":
      return yup
        .string()
        .max(maxCustomFieldStringChars)
        .label(label ?? "")
        .nullable()
        .transform((_, val) => {
          return val ? val : null;
        })
        .typeError((v) => `${v.label || v.path}: Value must be a string`);
    case "StringArray":
      return yup
        .array()
        .max(maxCustomFieldArray)
        .label(label ?? "")
        .of(convertCustomFieldDataTypeToYupShape("String", label).nonNullable())
        .transform((_, val) => (Array.isArray(val) && !!val.length ? val : null))
        .typeError("Array of strings required");
    case "Integer":
      return yup
        .number()
        .integer()
        .min(Number.MIN_SAFE_INTEGER)
        .max(Number.MAX_SAFE_INTEGER)
        .label(label ?? "")
        .nullable()
        .transform((_, val) => {
          return val ? +val : null;
        })
        .typeError((v) => `${v.label || v.path}: Value must be an integer`);
    case "IntegerArray":
      return yup
        .array()
        .max(maxCustomFieldArray)
        .label(label ?? "")
        .of(convertCustomFieldDataTypeToYupShape("Integer", label).nonNullable())
        .transform((_, val) => (Array.isArray(val) && !!val.length ? val : null))
        .typeError("Array of integers required");
    case "Float":
      return (
        yup
          .number()
          .min(Number.MIN_SAFE_INTEGER)
          .max(Number.MAX_SAFE_INTEGER)
          .label(label ?? "")
          // .test(
          //   "is-decimal",
          //   "A floating point number (dot separated decimal) is required",
          //   (val: any, { createError, path }) => {
          //     if (val) {
          //       const isValid = /^(?:\-?)\d*\.{1}\d*$/.test(val);
          //       if (!isValid)
          //         return createError({
          //           message: `${label || path}: Value must be a floating point number (dot separated decimal)`,
          //         });
          //       return true;
          //     }
          //     return true;
          //   }
          // )
          .nullable()
          .transform((_, val) => (val ? +val : null))
          .typeError((v) => `${v.label || v.path}: Value must be a floating point number (dot separated decimal)`)
      );
    case "FloatArray":
      return yup
        .array()
        .max(maxCustomFieldArray)
        .label(label ?? "")
        .of(convertCustomFieldDataTypeToYupShape("Float", label).nonNullable())
        .transform((_, val) => (Array.isArray(val) && !!val.length ? val : null))
        .typeError("Array of floats required");
    case "Boolean":
      return yup
        .bool()
        .label(label ?? "")
        .typeError((v) => `${v.label || v.path}: Value must be a boolean`);
    case "Date":
      return yup
        .mixed()
        .label(label ?? "")
        .nullable()
        .transform((_, val) => (val ? val : null))
        .test("correct", "A valid date of format YYYY[/.-]MM[/.-]DD is required", (value, { createError, path }) => {
          if (value === null || value === undefined) return true;
          if (typeof value === "string") {
            const isValid = /^\d{4}-\d{2}-\d{2}$/.test(value);
            if (!isValid)
              return createError({
                message: `${label || path}: A valid date of format YYYY[/.-]MM[/.-]DD is required`,
              });
            return true;
          }
          if (value instanceof Date && isValidDate(value)) return true;
          return false;
        })
        .typeError((v) => `${v.label || v.path}: Value must be of format MM[/.-]DD[/.-]YYYY`);
    case "DateArray":
      return yup
        .array()
        .max(maxCustomFieldArray)
        .label(label ?? "")
        .of(convertCustomFieldDataTypeToYupShape("Date", label).nonNullable())
        .transform((_, val) => (Array.isArray(val) && !!val.length ? val : null))
        .typeError("Array of dates required");

    case "DateTime":
      return yup
        .mixed()
        .label(label ?? "")
        .nullable()
        .transform((_, val) => (val ? val : null))
        .test(
          "correct",
          "Value must be of format YYYY[/.-]MM[/.-]DD HH:MM:SS AM|PM",
          (value, { createError, path }) => {
            if (value === null || value === undefined) return true;
            if (typeof value === "string") {
              const isValid = isISODate(value);
              if (!isValid)
                return createError({
                  message: `${label || path}: Value must be of format YYYY[/.-]MM[/.-]DD HH:MM:SS AM|PM`,
                });
              return true;
            }
            if (value instanceof Date) return true;
            return false;
          }
        )
        .typeError((v) => `${v.label || v.path}: Value must be of format YYYY[/.-]MM[/.-]DD HH:MM:SS AM|PM`);
    case "DateTimeArray":
      return yup
        .array()
        .max(maxCustomFieldArray)
        .label(label ?? "")
        .of(convertCustomFieldDataTypeToYupShape("DateTime", label).nonNullable())
        .transform((_, val) => (Array.isArray(val) && !!val.length ? val : null))
        .typeError("Array of datetimes required");
    case "Time":
      return yup
        .mixed()
        .label(label ?? "")
        .nullable()
        .transform((_, val) => (val ? val : null))
        .test(
          "correct",
          "A valid time of format Value must be of format HH:MM:SS AM|P is required",
          (value, { createError, path }) => {
            if (value === null || value === undefined) return true;
            if (typeof value === "string") {
              const isValid = isTime(value);
              if (!isValid)
                return createError({
                  message: `${label || path}: A valid time of format Value must be of format HH:MM:SS AM|P is required`,
                });
              return true;
            }
            if (value instanceof Date) return true;
            return false;
          }
        )
        .typeError((v) => `${v.label || v.path}: Value must be of format HH:MM:SS AM|PM`);
    case "TimeArray":
      return yup
        .array()
        .max(maxCustomFieldArray)
        .label(label ?? "")
        .of(convertCustomFieldDataTypeToYupShape("Time", label).nonNullable())
        .transform((_, val) => (Array.isArray(val) && !!val.length ? val : null))
        .typeError("Array of times required");
    case "DateTimeRange":
      return yup
        .array()
        .label(label ?? "")
        .of(yup.date().nonNullable())
        .min(2, "Two datetimes are required")
        .max(2, "Two datetimes are required")
        .typeError("Array of two datetimes required");
    case "TimeRange":
      return yup
        .number()
        .label(label ?? "")
        .nullable()
        .transform((_, val) => (val ? +val : null))
        .typeError((v) => `${v.label || v.path}: A valid timespan must be defined`);
    case "Url":
      return yup
        .string()
        .label(label ?? "")
        .nullable()
        .url()
        .transform((_, val) => {
          return val ? val : null;
        })
        .typeError((v) => `${v.label || v.path}: Value must be a string`);
    case "UrlArray":
      return yup
        .array()
        .max(maxCustomFieldArray)
        .label(label ?? "")
        .of(convertCustomFieldDataTypeToYupShape("Url", label).nonNullable())
        .transform((_, val) => (Array.isArray(val) && !!val.length ? val : null))
        .typeError("Array of strings required");
    case "Dataset":
    case "Sample":
    case "Project":
    case "Person":
    case "SharedContent":
    case "LabNotebook":
    case "LabNotebookExperiment":
    case "LabNotebookEntry":
    case "Attachment":
    case "InventoryItem":
      return yup
        .object()
        .label(label ?? "")
        .nullable()
        .shape({ id: yup.number().positive().integer(), name: yup.string() })
        .transform((_, val) => (val && !!Object.keys(val).length ? val : null))
        .typeError((v) => `${v.label || v.path}: A valid ${dataType} must be defined`);
    case "Method":
      return yup
        .object()
        .label(label ?? "")
        .nullable()
        .shape({ id: yup.string(), name: yup.string() })
        .transform((_, val) => (val && !!Object.keys(val).length ? val : null))
        .typeError((v) => `${v.label || v.path}: A valid ${dataType} must be defined`);
    case "DatasetArray":
    case "SampleArray":
    case "ProjectArray":
    case "PersonArray":
    case "MethodArray":
    case "SharedContentArray":
    case "LabNotebookArray":
    case "LabNotebookExperimentArray":
    case "LabNotebookEntryArray":
    case "InventoryItemArray":
      return yup
        .array()
        .max(maxCustomFieldArray)
        .label(label ?? "")
        .of(
          convertCustomFieldDataTypeToYupShape(
            dataType.replace("Array", "") as CustomField["dataType"],
            label
          ).nonNullable()
        )
        .transform((_, val) => (Array.isArray(val) && !!val.length ? val : null))
        .typeError("An array of entities must be defined");
    case "Barcode":
      return yup
        .string()
        .max(maxCustomFieldStringChars)
        .label(label ?? "")
        .nullable()
        .transform((_, val) => {
          return val ? val : null;
        })
        .typeError((v) => `${v.label || v.path}: Value must be a string`);
    default:
      console.error("Unsupported data type", dataType);
      return yup.mixed().label(label ?? "");
  }
};

export const customFieldToCustomFieldForm = (
  initialValues: Partial<CustomField> | undefined
): Partial<CustomFieldForm> => {
  const result = {
    ...initialValues,
    dataType: initialValues?.dataType,
    predefinedOptions: convertPredefinedOptionsEntity2Form(initialValues?.predefinedOptions, initialValues?.dataType),
    defaultValues: convertNativeCustomFieldDataTypeToFormValue(initialValues?.defaultValues, initialValues?.dataType),
    ...generateAbstractedFromDataType(initialValues?.dataType),
  };
  // console.log("Field --> Form", initialValues, "-->", result);

  return result;
};
export const customFieldFormToCustomField = (entity: Partial<CustomFieldForm>): Partial<CustomField> => {
  const result = {
    ...entity,
    dataType: entity.dataType!,
    predefinedOptions: convertPredefinedOptionsForm2Entity(entity.predefinedOptions, entity.dataType),
    defaultValues: convertFormValueToNativeCustomFieldDataType(entity.defaultValues, entity.dataType),
  };
  // console.log("Form --> Field", entity, "-->", result);

  return result;
};

export const abstractedDataTypeToFilter = (dataType: AbstractedCustomFieldDataTypes): CustomFieldDataType[] => {
  switch (dataType) {
    case "Text":
      return ["String", "StringArray"];
    case "URL":
      return ["Url", "UrlArray"];
    case "Number":
      return ["Integer", "IntegerArray", "Float", "FloatArray"];
    case "Toggle":
      return ["Boolean"];
    case "Date":
      return ["Date", "DateArray", "DateTime", "DateTimeArray", "DateTimeRange"];
    case "Time":
      return ["Time", "TimeArray"];
    case "Entity Reference":
      return [
        "Dataset",
        "Sample",
        "Project",
        "Person",
        "Method",
        "SharedContent",
        "LabNotebook",
        "LabNotebookExperiment",
        "LabNotebookEntry",
        "InventoryItem",
        "DatasetArray",
        "SampleArray",
        "ProjectArray",
        "PersonArray",
        "MethodArray",
        "SharedContentArray",
        "LabNotebookArray",
        "LabNotebookExperimentArray",
        "LabNotebookEntryArray",
        "InventoryItemArray",
      ];
    case "Barcode":
      return ["Barcode"];
    case "Attachment":
      return ["Attachment"];
    default:
      return [];
  }
};

export const customFieldDataTypeToColumnWidth = (dataType: CustomFieldDataType): number => {
  switch (dataType) {
    case "String":
    case "StringArray":
    case "Integer":
    case "IntegerArray":
    case "Float":
    case "FloatArray":
    case "Date":
    case "DateArray":
    case "DateTime":
    case "DateTimeArray":
    case "Time":
    case "TimeArray":
    case "DateTimeRange":
    case "Dataset":
    case "DatasetArray":
    case "Sample":
    case "SampleArray":
    case "Project":
    case "ProjectArray":
    case "Person":
    case "PersonArray":
    case "Method":
    case "MethodArray":
    case "SharedContent":
    case "SharedContentArray":
    case "LabNotebook":
    case "LabNotebookArray":
    case "LabNotebookExperiment":
    case "LabNotebookExperimentArray":
    case "LabNotebookEntry":
    case "Barcode":
    case "Url":
    case "UrlArray":
    case "Attachment":
      return 700;
    case "TimeRange":
      return 800;
    case "Boolean":
      return 200;
    case "InventoryItem":
    case "InventoryItemArray":
      return 500;
    default:
      return 400;
  }
};
